import {
	ArrowRight,
	Compass,
	DollarSign,
	FolderPlus,
	MessageCircle,
	Sparkles,
	Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type {
	ContractorProfile,
	DesignerProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	browseContractors,
	browseDesigners,
} from "@/features/marketplace/services/marketplace-api.service";
import { getHomeownerProfile } from "@/features/settings/services/settings-api.service";
import { StudioButton } from "@/features/studio/theme";
import { QuickActionCard } from "./dashboard-widgets";
import { DesignerPreviewCard } from "./designer-preview-card";
import { HOME_SEARCH_SUGGESTIONS, SearchBar } from "./homeowner-hero";

const ASSETS_URL = import.meta.env.VITE_ASSETS_URL as string;

interface NewUserHeroProps {
	userName: string;
}

function NewUserHero({ userName: _userName }: NewUserHeroProps) {
	const getGreeting = () => {
		const hour = new Date().getHours();
		if (hour >= 5 && hour < 12) return "Good morning,";
		if (hour >= 12 && hour < 17) return "Good afternoon,";
		if (hour >= 17 && hour < 24) return "Good evening,";
		return "Good night,";
	};

	return (
		<section
			className="dashboard-container"
			style={{ padding: "72px 40px 56px" }}
		>
			<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted">
				VOL. I · ISSUE No. 01 · YOUR FIRST CHAPTER
			</p>

			<h1 className="mt-5 font-display font-medium text-[56px] leading-none tracking-tighter text-dashboard-text">
				{getGreeting()}
				<br />
				<span className="font-display italic font-medium text-[120px] leading-[0.85] tracking-tighter text-dashboard-accent-display">
					let's begin.
				</span>
			</h1>

			<div className="mt-9 grid grid-cols-[120px_1fr] gap-8">
				<div className="mt-4 h-px bg-dashboard-warm-300" />

				<p className="max-w-2xl font-body text-[18px] leading-[1.6] text-dashboard-text">
					Welcome to Finishd — where a renovation becomes a finished home.
					<br />
					Start by gathering{" "}
					<span className="font-display italic font-medium text-lg text-dashboard-muted-dark tracking-tight">
						inspiration
					</span>
					, meet{" "}
					<span className="font-display italic font-medium text-lg text-dashboard-muted-dark tracking-tight">
						verified designers
					</span>{" "}
					near you, and create your{" "}
					<span className="font-display italic font-medium text-lg text-dashboard-muted-dark tracking-tight">
						first project
					</span>{" "}
					whenever you're ready.
				</p>
			</div>
		</section>
	);
}

interface DesignerBannerProps {
	city?: string;
	designerCount: number;
	contractorCount: number;
}

function DesignerBanner({
	city,
	designerCount,
	contractorCount,
}: DesignerBannerProps) {
	const navigate = useNavigate();
	const total = designerCount + contractorCount;

	return (
		<section className="dashboard-container" style={{ padding: "0 40px 24px" }}>
			<div className="relative aspect-16/7 overflow-hidden rounded dashboard-hero-image">
				<img
					src={`${ASSETS_URL}siteAfter.jpeg`}
					alt=""
					className="absolute inset-0 h-full w-full object-cover"
					style={{ filter: "saturate(0.92) contrast(1.08) brightness(0.95)" }}
				/>
				<div className="absolute inset-0 dashboard-overlay-bottom" />

				<div className="absolute top-8 left-9 text-dashboard-on-fill">
					{city && (
						<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-dashboard-homecard">
							NEAR YOU · {city.toUpperCase()}
						</p>
					)}
					<p
						className="mt-2 font-display font-medium text-[38px] text-dashboard-on-fill tracking-tight"
						style={{ textShadow: "rgba(0, 0, 0, 0.4) 0px 2px 12px" }}
					>
						Designers & contractors near you
					</p>
					{total > 0 && (
						<p className="mt-1.5 font-body text-[13px] text-dashboard-homecard tracking-wide">
							{total} verified professionals
						</p>
					)}
				</div>

				<div className="absolute bottom-9 right-9 text-right text-dashboard-on-fill">
					<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-dashboard-homecard">
						WHERE TO START
					</p>
					<p className="mt-1 font-display italic font-medium text-[28px] text-dashboard-on-fill tracking-tight">
						Find your match.
					</p>
					<p className="mt-1 font-body text-[12px] text-dashboard-homecard">
						Filter by style, budget & service area.
					</p>
				</div>
			</div>

			<div
				className="relative -mt-8.5 mx-10 rounded-2xl border border-dashboard-border px-7 py-5 grid grid-cols-[1fr_auto] gap-9 items-center dashboard-stats-bar"
				style={{ background: "var(--dashboard-gradient-surface)" }}
			>
				<div
					className="relative pr-1"
					style={{
						borderRight:
							"1px solid color-mix(in srgb, var(--color-border-default) 55%, transparent)",
						marginRight: "-8px",
					}}
				>
					<div className="flex items-baseline gap-3">
						<span className="font-display font-medium text-[28px] text-dashboard-text tabular-nums leading-none tracking-tight">
							{total}
						</span>
						<span className="font-display italic text-sm text-dashboard-muted tracking-tight">
							verified pros
						</span>
					</div>
					{city && (
						<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.22em] text-dashboard-muted mt-2">
							{city.toUpperCase()}
						</p>
					)}
					<div className="flex gap-4 mt-4">
						<div>
							<p className="font-display font-medium text-[18px] text-dashboard-text leading-none tracking-tight">
								{designerCount}
							</p>
							<p className="font-mono text-[8px] font-semibold uppercase tracking-[0.16em] text-dashboard-muted mt-0.5">
								DESIGNERS
							</p>
						</div>
						<div className="w-px bg-dashboard-border/60" />
						<div>
							<p className="font-display font-medium text-[18px] text-dashboard-text leading-none tracking-tight">
								{contractorCount}
							</p>
							<p className="font-mono text-[8px] font-semibold uppercase tracking-[0.16em] text-dashboard-muted mt-0.5">
								CONTRACTORS
							</p>
						</div>
					</div>
				</div>

				<div className="flex flex-row relative z-10  gap-2 ">
					<StudioButton
						className="w-30"
						variant="secondary"
						size="md"
						style={{
							height: 44,
							paddingLeft: 0,
							paddingRight: 0,
							fontSize: 13.5,
							fontWeight: 600,
							letterSpacing: "0.025em",
							borderRadius: "var(--radius-xl)",
						}}
						onClick={() => navigate("/public-projects")}
					>
						Browse all
					</StudioButton>
					<button
						type="button"
						className="dashboard-primary-button flex h-11  justify-center  w-40 rounded-xl font-body text-[13.5px] font-semibold tracking-wide border-none text-dashboard-on-fill cursor-pointer items-center gap-2 transition-all duration-200 hover:-translate-y-px"
						onClick={() => navigate("/designers")}
					>
						Find a designer
						<ArrowRight className="h-3 w-3" />
					</button>
				</div>
			</div>
		</section>
	);
}

// The two "Coming soon" tiles have no navigation target: the features they
// name do not exist yet, so the tiles do not route anywhere.
const QUICK_ACTIONS_NEW_USER = [
	{
		icon: FolderPlus,
		title: "Create project",
		subtitle: "Start your renovation journey",
		navigateTo: "/projects/new",
	},
	{
		icon: Compass,
		title: "Browse inspiration",
		subtitle: "Rooms from real projects",
		navigateTo: "/inspire",
	},
	{
		icon: MessageCircle,
		title: "Talk to designer",
		subtitle: "",
		navigateTo: "/designers",
	},
	{
		icon: DollarSign,
		title: "Compare budget",
		subtitle: "Plan costs for a new project",
		navigateTo: "/projects/new",
	},
	{
		icon: Upload,
		title: "Upload a room",
		subtitle: "Coming soon",
		navigateTo: null,
	},
	{
		icon: Sparkles,
		title: "AI room analysis",
		subtitle: "Coming soon",
		navigateTo: null,
	},
] as const;

interface NewUserHomePageProps {
	userName: string;
}

export function NewUserHomePage({ userName }: NewUserHomePageProps) {
	const navigate = useNavigate();
	const [searchQuery, setSearchQuery] = useState("");
	const [designers, setDesigners] = useState<DesignerProfile[]>([]);
	const [contractors, setContractors] = useState<ContractorProfile[]>([]);
	const [designerCount, setDesignerCount] = useState(0);
	const [contractorCount, setContractorCount] = useState(0);
	const [userCity, setUserCity] = useState<string | undefined>(undefined);
	const [isScrolledRight, setIsScrolledRight] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	const MAX_DESIGNERS = 5;
	const MAX_CONTRACTORS = 5;

	useEffect(() => {
		async function load() {
			try {
				const profileResult = await getHomeownerProfile();
				const city = profileResult?.city;
				setUserCity(city);

				const [
					designerCardsResult,
					contractorCardsResult,
					designerCountResult,
					contractorCountResult,
				] = await Promise.all([
					browseDesigners({ city, limit: MAX_DESIGNERS }),
					browseContractors({ city, limit: MAX_CONTRACTORS }),
					browseDesigners({ city, limit: 1 }),
					browseContractors({ city, limit: 1 }),
				]);

				setDesigners(designerCardsResult.designers || []);
				setContractors(contractorCardsResult.contractors || []);
				setDesignerCount(designerCountResult.total || 0);
				setContractorCount(contractorCountResult.total || 0);
			} catch (err) {
				console.error("Failed to load dashboard data:", err);
				toast.error("Failed to load dashboard data");
			}
		}
		load();
	}, []);

	const handleScroll = () => {
		if (!scrollRef.current) return;
		const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
		setIsScrolledRight(scrollLeft + clientWidth >= scrollWidth - 10);
	};

	const handleSearch = (query: string) => {
		navigate(`/public-projects?search=${encodeURIComponent(query)}`);
	};

	const handleSuggestionClick = (query: string) => {
		setSearchQuery(query);
		handleSearch(query);
	};

	const handleViewProfile = (id: string, type: "DESIGNER" | "CONTRACTOR") => {
		if (type === "DESIGNER") {
			navigate(`/designers/${id}`);
		} else {
			navigate(`/contractors/${id}`);
		}
	};

	const allPros = [
		...designers.map((d) => ({ profile: d, type: "DESIGNER" as const })),
		...contractors.map((c) => ({ profile: c, type: "CONTRACTOR" as const })),
	];

	return (
		<div className="min-h-screen bg-dashboard-surface">
			<NewUserHero userName={userName} />

			<SearchBar
				value={searchQuery}
				onChange={setSearchQuery}
				suggestions={HOME_SEARCH_SUGGESTIONS}
				onSuggestionClick={handleSuggestionClick}
				onSearch={handleSearch}
			/>

			<DesignerBanner
				city={userCity}
				designerCount={designerCount}
				contractorCount={contractorCount}
			/>

			<section
				className="dashboard-container"
				style={{ padding: "32px 40px 0px" }}
			>
				<div className="grid grid-cols-6 gap-[14px]">
					{QUICK_ACTIONS_NEW_USER.map((action) => (
						<QuickActionCard
							key={action.title}
							icon={action.icon}
							title={action.title}
							subtitle={action.subtitle}
							onClick={
								action.navigateTo
									? () => navigate(action.navigateTo)
									: undefined
							}
						/>
					))}
				</div>
			</section>

			<section
				className="dashboard-container"
				style={{ padding: "100px 40px 24px" }}
			>
				<div className="mb-9 flex items-end justify-between">
					<div>
						<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
							VERIFIED · NEAR YOU
						</p>
						<h2 className="mt-2 font-display font-medium text-[42px] leading-[1.05] tracking-tight text-dashboard-text">
							Designers & contractors{" "}
							<span className="font-display italic font-medium text-dashboard-accent-display tracking-tight">
								near you.
							</span>
						</h2>
					</div>
					{isScrolledRight && (
						<div className="flex gap-2 pb-1">
							<StudioButton
								variant="secondary"
								size="sm"
								style={{
									height: 40,
									paddingLeft: 16,
									paddingRight: 16,
									fontSize: 12,
									fontWeight: 600,
									borderRadius: "var(--radius-xl)",
								}}
								onClick={() => navigate("/designers")}
							>
								More designers
								<ArrowRight className="h-3 w-3" />
							</StudioButton>
							<StudioButton
								variant="secondary"
								size="sm"
								style={{
									height: 40,
									paddingLeft: 16,
									paddingRight: 16,
									fontSize: 12,
									fontWeight: 600,
									borderRadius: "var(--radius-xl)",
								}}
								onClick={() => navigate("/contractors")}
							>
								More contractors
								<ArrowRight className="h-3 w-3" />
							</StudioButton>
						</div>
					)}
				</div>

				<div
					ref={scrollRef}
					className="flex gap-5 overflow-x-auto scrollbar-hide"
					onScroll={handleScroll}
					style={{ scrollSnapType: "x mandatory" }}
				>
					{allPros.map((pro) => (
						<div
							key={pro.profile.id}
							className="shrink-0 w-85"
							style={{ scrollSnapAlign: "start" }}
						>
							<DesignerPreviewCard
								profile={pro.profile}
								type={pro.type}
								onViewProfile={(id) => handleViewProfile(id, pro.type)}
							/>
						</div>
					))}
				</div>
			</section>

			<div
				className="dashboard-container"
				style={{ padding: "60px 40px 80px", textAlign: "center" }}
			>
				<span className="font-display italic font-semibold text-[16px] text-dashboard-muted tracking-tight">
					Because starting is common. Finishing well is not.
				</span>
			</div>
		</div>
	);
}
